// @flow

import React from "react";
import { localize } from "redux-i18n";

import { View, Col, Button, SchemaTable } from "@components";
import type { ServiceKpis } from "@modules/service/types";
import config from "@config";
type OwnProps = $Exact<{
  schema: Object,
  title?: string,
  data: ?Array<ServiceKpis>,
  disabled?: boolean,
  onChange?: Function,
  kpiTitle?: string,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class CommonComplianceKpisForm extends React.Component<Props> {
  get default() {
    return {
      kpiMeasure: 168, // 7 days in hours
      activeFrom: "",
      activeTo: "",
      id: null,
    };
  }

  get data(): Array<ServiceKpis> {
    return this.props.data || [this.default];
  }

  get schema() {
    return this.props.schema;
  }

  isValidDate(dateString: string): boolean {
    return Date.parse(dateString) > 0;
  }

  isLiveRecord(item: ServiceKpis): boolean {
    return item.isLive === true;
  }

  itemIndex(item: ServiceKpis) {
    return this.data.findIndex(
      itm =>
        item.kpiMeasure === itm.kpiMeasure &&
        item.activeFrom === itm.activeFrom &&
        item.activeTo === itm.activeTo,
    );
  }

  isItLastButNotSingle(item: ServiceKpis): boolean {
    return this.itemIndex(item) !== this.data.length - 1 && this.data.length > 1;
  }

  get uiSchema() {
    return {
      "ui:order": ["kpiMeasure", "activeFrom", "activeTo"],
      kpiMeasure: {
        "ui:widget": "number",
        "ui:title": this.props.kpiTitle ? this.props.kpiTitle : this.props.t("KPI_TARGET_HOURS"),
        "ui:options": {
          required: true,
          minWidth: "35%",
          disabled: (item: ServiceKpis) => this.isItLastButNotSingle(item),
          minimum: 1,
        },
      },
      activeFrom: {
        "ui:widget": "date-picker",
        "ui:title": this.props.t("EFFECTIVE_FROM"),
        "ui:options": {
          required: true,
          minWidth: "25%",
          dateFormat: config.dateFormatBE,
          disabled: (item: ServiceKpis) =>
            this.isItLastButNotSingle(item) || this.isLiveRecord(item),
          minDate: (item: ServiceKpis) => {
            const prevRow = this.data[this.itemIndex(item) - 1];
            return prevRow && prevRow.activeTo
              ? new Date(new Date(prevRow.activeTo).getTime() + 8640000).toISOString()
              : false;
          },
        },
      },
      activeTo: {
        "ui:widget": "date-picker",
        "ui:title": this.props.t("EFFECTIVE_TO"),
        "ui:options": {
          minWidth: "25%",
          dateFormat: config.dateFormatBE,
          disabled: (item: ServiceKpis) => this.isItLastButNotSingle(item),
          minDate: (item: ServiceKpis) => {
            const fromTime = new Date(item.activeFrom || new Date()).getTime();
            try {
              return new Date(fromTime - (fromTime % 86400000)).toISOString();
            } catch (e) {
              return new Date(new Date().getTime() + 8640000).toISOString();
            }
          },
        },
      },
    };
  }

  get canAdd() {
    let prev: ServiceKpis = this.data[this.data.length - 1];

    if (this.data.length < 1) return true;

    return (
      this.isValidDate(prev.activeFrom) &&
      this.isValidDate(prev.activeTo || "") &&
      prev.kpiMeasure > 0
    );
  }

  handleChange = (data: Array<any>) => {
    const next: Array<ServiceKpis> = data;

    // If the record is being deleted, we need make prev.activeTo = null
    if (data.length < this.data.length) {
      next[next.length - 1].activeTo = null;
    }

    if (this.props.onChange) {
      this.props.onChange(next);
    }
  };

  handleAdd = () => {
    const last = this.data[this.data.length - 1];
    const next = {
      ...this.default,
      activeFrom:
        last && last.activeTo
          ? new Date(new Date(last.activeTo).getTime() + 86400000).toISOString()
          : new Date().toISOString(),
      activeTo: null,
    };

    this.handleChange([...this.data, next]);
  };

  render() {
    const tableData: Array<Object> = [...this.data];

    return (
      <View>
        {this.props.title ? <h6>{this.props.title}</h6> : null}
        {this.props.disabled ? null : (
          <View justifyContent="end" direction="row">
            <Button
              className="link record-table__add-button"
              onClick={this.handleAdd}
              disabled={!this.canAdd}
            >
              <span className="material-icons font-size-26 mr-1">add</span>
              {this.props.t("RETURN_TO_WORK_DETAILS_COMPLIANCE_KPIS_ADD_NEW")}
            </Button>
          </View>
        )}
        {this.schema && this.uiSchema && (
          <SchemaTable
            tableData={tableData}
            schema={this.schema}
            uiSchema={this.uiSchema}
            onChange={this.handleChange}
            disabled={!!this.props.disabled}
            allowDeletes
            allowDeletesFn={(item: ServiceKpis) => {
              return (
                !this.isLiveRecord(item) &&
                !this.isItLastButNotSingle(item) &&
                this.itemIndex(item) > 0
              );
            }}
          />
        )}
      </View>
    );
  }
}

export default localize()(CommonComplianceKpisForm);
